import { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';

import { useCategoryBanner } from '../../../../../hooks/queries/category/useCategoryBanner';
import { useUpsertCategoryBannerMutate } from '../../../../../hooks/queries/category/useUpsertCategoryBannerMutate';
import { useDeleteCategoryBannerMutate } from '../../../../../hooks/queries/category/useDeleteCategoryBannerMutate';
import { asyncSimpleModal } from '../../../../../lib/common';
import categoryToastMsg from '../../../../../constants/service/toastMsg/categoryToastMsg';
import { CategoryBannerResponse } from '../../../../../lib/api/category/categoryBannerType';
import { CategoryBannerSlideFormItem } from '../types';
import { responseToFormItems, formItemsToRequest, isSlideIncomplete } from '../utils/payloadConverter';
import { getInnerRules, bannerFormRules } from '../utils/formRules';

type UseCategoryBannerFormProps = {
  form: WrappedFormUtils;
};

export const useCategoryBannerForm = ({ form }: UseCategoryBannerFormProps) => {
  const [isEditable, setIsEditable] = useState(false);
  const [currentTab, setCurrentTab] = useState<number>();

  const { data, isLoading, isError } = useCategoryBanner();

  /**
   * 서버 데이터를 antd form 상태로 동기화한다.
   * 주의: 이 함수는 FormWithTab이 mount된 뒤에 호출되어야 'slides' 필드가
   * getFieldDecorator로 등록된 상태에서 값이 정상 반영된다.
   */
  const syncFormWithData = (bannerData: CategoryBannerResponse | undefined) => {
    if (!bannerData || bannerData.length === 0) {
      form.setFieldsValue({ slides: [] });
      setCurrentTab(undefined);
      return;
    }
    const items = responseToFormItems(bannerData);
    form.setFieldsValue({ slides: items });
    setCurrentTab(items[0]?.id);
  };

  // save/delete 성공 시 view 모드로 전환만 한다. 폼 동기화는 다음 편집 진입 시 useEffect가
  // setQueryData로 갱신된 data를 기반으로 수행하므로 여기서 syncFormWithData를 중복 호출하지 않는다.
  const upsertMutation = useUpsertCategoryBannerMutate(() => {
    setIsEditable(false);
  });
  // 빈 상태로 저장하면 내부적으로 DELETE를 호출하지만, 사용자 관점에선 "저장" 동작이므로 토스트 문구를 통일한다.
  const deleteMutation = useDeleteCategoryBannerMutate(() => {
    setIsEditable(false);
  }, categoryToastMsg.UPSERT_CATEGORY_BANNER_SUCCESS);

  /**
   * 편집 모드 진입 시점에 서버 데이터를 폼에 주입.
   * antd compatible form의 setFieldsValue는 Form.Item(getFieldDecorator) 등록 후에만 동작하므로,
   * setIsEditable(true)로 FormWithTab을 mount시킨 다음 useEffect(commit 후)에서 sync해야 한다.
   * (React "외부 시스템과의 동기화" 케이스 — useEffect의 정당한 사용)
   */
  useEffect(() => {
    if (isEditable) {
      syncFormWithData(data);
    }
    // isEditable 전환 시점에만 sync. data 변경에 반응하면 편집 중 refetch로 편집값이 덮어써질 수 있어 의도적으로 제외.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditable]);

  const handleStartEdit = () => {
    setIsEditable(true);
  };

  // 빈 배열 PUT은 백엔드가 400으로 거부(CATEGORY091)하므로, 빈 상태 저장은 DELETE로 라우팅한다.
  // 단, 서버에도 데이터가 없는 상태(=초기 생성 흐름에서 빈 채로 저장)면 의미 없는 DELETE를 보내지 않고 그대로 종료한다.
  const handleFinish = () => {
    const slides = form.getFieldValue('slides') as CategoryBannerSlideFormItem[] | undefined;
    const hasNoSlides = !slides || slides.length === 0;

    if (hasNoSlides) {
      const hadServerData = !!data && data.length > 0;
      if (hadServerData) {
        deleteMutation.mutate();
      } else {
        setIsEditable(false);
      }
      return;
    }

    // FormWithTab.validateTab의 callback race로 검증 실패에도 onFinish가 호출되는 케이스가 있어
    // 백엔드 호출 직전에 명시적으로 한 번 더 차단한다. 검증 메시지는 이미 antd가 화면에 띄워준다.
    if (slides.some(isSlideIncomplete)) return;

    upsertMutation.mutate(formItemsToRequest(slides));
  };

  const handleCancel = async () => {
    const confirmed = await asyncSimpleModal({
      type: 'confirm',
      title: '편집 취소',
      message: '변경사항이 저장되지 않습니다.\n계속 진행하시겠습니까?',
    });
    if (confirmed) {
      setIsEditable(false);
    }
  };

  return {
    data,
    isLoading,
    isError,
    isEditable,
    handleStartEdit,
    currentTab,
    setCurrentTab,
    isSaving: upsertMutation.isLoading,
    isDeleting: deleteMutation.isLoading,
    handleFinish,
    handleCancel,
    getInnerRules,
    formRules: bannerFormRules,
  };
};
